import { useState } from "react";

import { Box, Button, HStack, IconButton, Text } from "@chakra-ui/react";

import { useFieldArray, useFormContext } from "react-hook-form";
import { LiaPlusSolid, LiaTrashSolid } from "react-icons/lia";

import { BankStatementUpload } from "@/jenesys-types/jenesys-entities/config";

import { InteractiveTable } from "@/components/common/table/InteractiveTable/InteractiveTable";
import { InteractiveColumnDefinition } from "@/components/common/table/InteractiveTable/useInteractiveTable";
import { FormInput } from "@/components/forms/FormBaseInput/FormBaseInput";
import { FormDatePicker } from "@/components/forms/FormDatePicker/FormDatePicker";
import { FormField } from "@/components/forms/FormField/FormField";

const initialLine = {
  Date: "",
  Description: "",
  Amount: "",
  Supplier: ""
};

export const StatementUploadEditTable = () => {
  const [selectedLines, setSelectedLines] = useState<string[]>([]);
  const { control, watch } = useFormContext<BankStatementUpload>();
  const isConfirmed = watch("status") === "CONFIRMED";

  const { fields, remove, append } = useFieldArray({
    name: "extracted_details",
    control
  });

  const addLine = () => {
    append(initialLine);
  };

  const handleDeleteLines = () => {
    const indicesToDelete = selectedLines.map((id) =>
      fields.findIndex((field) => field.id === id)
    );

    remove(indicesToDelete);
    setSelectedLines([]);
  };

  // remove from list and selected lines
  const handleDeleteSingleLine = (index: number) => {
    remove(index);
    setSelectedLines(selectedLines.filter((id) => id !== fields[index].id));
  };

  const columnDefinitions: InteractiveColumnDefinition<
    (typeof fields)[number]
  >[] = [
    {
      id: "Date",
      header: "Date",
      accessor: (field) => {
        const index = fields.findIndex((f) => f.id === field.id);
        return (
          <FormField
            isDisabled={isConfirmed}
            label="Date"
            name={`extracted_details.${index}.Date`}
            control={FormDatePicker}
            size="sm"
          />
        );
      }
    },
    {
      id: "Description",
      header: "Description",
      accessor: (field) => {
        const index = fields.findIndex((f) => f.id === field.id);
        return (
          <FormField
            isDisabled={isConfirmed}
            label="Description"
            name={`extracted_details.${index}.Description`}
            control={FormInput}
            borderColor="gray.300"
            rounded={8}
            size="sm"
          />
        );
      }
    },
    {
      id: "Amount",
      header: "Amount",
      accessor: (field) => {
        const index = fields.findIndex((f) => f.id === field.id);
        return (
          <FormField
            isDisabled={isConfirmed}
            label="Amount"
            name={`extracted_details.${index}.Amount`}
            control={FormInput}
            type="number"
            borderColor="gray.300"
            rounded={8}
            size="sm"
          />
        );
      }
    },
    {
      id: "Supplier",
      header: "Supplier",
      accessor: (field) => {
        const index = fields.findIndex((f) => f.id === field.id);
        return (
          <FormField
            isDisabled={isConfirmed}
            label="Supplier"
            name={`extracted_details.${index}.Supplier`}
            control={FormInput}
            borderColor="gray.300"
            rounded={8}
            size="sm"
          />
        );
      }
    },
    {
      id: "actions",
      header: "",
      accessor: (field) => {
        const index = fields.findIndex((f) => f.id === field.id);
        return (
          <IconButton
            onClick={() => handleDeleteSingleLine(index)}
            fontSize={20}
            isDisabled={fields.length === 1 || isConfirmed}
            aria-label="Delete"
            icon={<LiaTrashSolid />}
            variant="default"
            colorScheme="default"
            size="sm"
          />
        );
      }
    }
  ];

  return (
    <Box border="1px solid" borderColor="gray.200" pb={4} mt={3.5}>
      <InteractiveTable
        items={fields}
        columnDefinitions={columnDefinitions}
        rowSelectionMode="multi"
        selectedItemIds={selectedLines}
        onSelectedItemsChange={setSelectedLines}
      />
      <Button
        ml={4}
        isDisabled={isConfirmed}
        leftIcon={<LiaPlusSolid />}
        fontWeight={500}
        variant="link"
        size="sm"
        mt={3.5}
        color="black"
        onClick={addLine}
      >
        Add New Line
      </Button>

      {!!selectedLines.length && (
        <Box
          position="fixed"
          bottom={8}
          left="50%"
          transform="translateX(-50%)"
          bg="white"
          boxShadow="md"
          w="fit-content"
          p={3}
          rounded="xl"
          zIndex={1}
        >
          <HStack fontWeight={500} spacing={5}>
            <Text fontSize="sm" fontWeight="semibold">
              {selectedLines.length} Selected
            </Text>

            <Button
              size="sm"
              isDisabled={isConfirmed}
              onClick={handleDeleteLines}
              borderRadius="md"
              variant="outline"
              fontWeight="semibold"
              isLoading={false}
            >
              Delete
            </Button>
          </HStack>
        </Box>
      )}
    </Box>
  );
};
